import type { SessionProfile } from "../../shared/types/domain";
import { demoOrganizationRepository } from "../organization/demoOrganizationRepository";
import { demoSalesRefundAdapter } from "../sales/demoSalesRepository";
import { demoSchedulingRepository } from "../scheduling/demoSchedulingRepository";
import { demoAuditAdapter, demoNotificationAdapter } from "../governance/demoGovernanceRepository";
import type { RefundState } from "./refunds.types";
const KEY = "boom-v4-refunds",
  seed: RefundState = { requests: [] };
const clone = <T>(x: T): T => structuredClone(x);
function load() {
  const raw = localStorage.getItem(KEY);
  if (!raw) return clone(seed);
  try {
    return JSON.parse(raw) as RefundState;
  } catch {
    return clone(seed);
  }
}
function save(s: RefundState) {
  localStorage.setItem(KEY, JSON.stringify(s));
}
function fail(m: string): never {
  throw new Error(m);
}
async function context(a: SessionProfile) {
  const org = await demoOrganizationRepository.getState();
  if (!org.accounts.some((x) => x.id === a.id && x.status === "active"))
    fail("当前账号不可用。");
  return org;
}
export const demoRefundRepository = {
  async getState() {
    return load();
  },
  async reset() {
    localStorage.removeItem(KEY);
    return clone(seed);
  },
  async createRequest(
    actor: SessionProfile,
    input: { orderId: string; refundUnits: number; reason: string },
  ) {
    await context(actor);
    if (!["member", "employee", "manager", "super"].includes(actor.role))
      fail("当前角色不能申请退款。");
    const ctx = await demoSalesRefundAdapter.getContext(input.orderId);
    if (
      !ctx ||
      !ctx.payment ||
      !ctx.coursePackage ||
      !["paid", "partially_refunded"].includes(ctx.order.status)
    )
      fail("只有已收款且仍有余额的订单可以退款。");
    if (actor.role === "member" && ctx.order.memberId !== actor.id)
      fail("只能申请本人的订单退款。");
    if (
      actor.role !== "member" &&
      actor.role !== "super" &&
      ctx.order.storeId !== actor.storeId
    )
      fail("不能申请其他门店订单退款。");
    const refundableUnits = ctx.coursePackage.totalUnits - ctx.coursePackage.usedUnits;
    if (
      !Number.isInteger(input.refundUnits) ||
      input.refundUnits <= 0 ||
      input.refundUnits !== refundableUnits
    )
      fail("退款必须一次申请全部剩余可退课时。");
    if (new Date(ctx.coursePackage.validTo).getTime() < Date.now()) fail("课程包已过期，不支持退款。");
    if (input.reason.trim().length < 4) fail("请填写完整退款原因。");
    const scheduling = await demoSchedulingRepository.getState();
    const reserved = scheduling.schedules.filter(
      (x) =>
        x.packageId === ctx.coursePackage!.id &&
        ["scheduled", "checked_in"].includes(x.status),
    ).length;
    if (
      ctx.coursePackage.totalUnits -
        ctx.coursePackage.usedUnits -
        input.refundUnits <
      reserved
    )
      fail("退款后课时不足以覆盖已预约课程，请先取消相关排课。");
    const state = load();
    if (
      state.requests.some(
        (x) => x.orderId === input.orderId && x.status === "pending",
      )
    )
      fail("该订单已有待审批退款。");
    const paidAt = new Date(ctx.order.paidAt ?? ctx.payment.paidAt).getTime();
    const withinSevenDays = Date.now() - paidAt <= 7 * 24 * 60 * 60 * 1000;
    let amount: number;
    let calculationRule: string;
    if (ctx.order.pricing.packageType === "time_card") {
      const durationDays = ctx.order.pricing.durationDays;
      const startDate = new Date(ctx.coursePackage.validFrom);
      const todayUtc = Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());
      const startUtc = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());
      const usedDays = Math.min(durationDays, Math.max(0, Math.floor((todayUtc - startUtc) / 86_400_000)));
      const remainingAmount = ctx.payment.amountCents * (durationDays - usedDays) / durationDays;
      amount = withinSevenDays && usedDays === 0 ? ctx.payment.amountCents : Math.floor(remainingAmount * 0.8);
      calculationRule = withinSevenDays && usedDays === 0 ? "7天内未使用：实收全额退款" : "时效卡剩余天数价值×80%";
    } else {
      const paidUnits = ctx.order.pricing.lessonCount + ctx.order.pricing.giftLessons;
      const actualUnitPrice = ctx.payment.amountCents / paidUnits;
      const remainingAmount = Math.max(0, ctx.payment.amountCents - ctx.coursePackage.usedUnits * actualUnitPrice);
      amount = ctx.coursePackage.usedUnits === 0 && withinSevenDays ? ctx.payment.amountCents : Math.floor(remainingAmount * 0.8);
      calculationRule = ctx.coursePackage.usedUnits === 0 && withinSevenDays ? "7天内未使用：实收全额退款" : "（实收-已耗课时×实际课时单价）×80%";
    }
    if (amount <= 0) fail("当前课程包没有可退金额。");
    const next = {
      id: crypto.randomUUID(),
      refundNumber: `RF-${Date.now()}`,
      orderId: ctx.order.id,
      packageId: ctx.coursePackage.id,
      memberId: ctx.order.memberId,
      storeId: ctx.order.storeId,
      requestedBy: actor.id,
      refundUnits: input.refundUnits,
      refundAmountCents: amount,
      calculationRule,
      reason: input.reason.trim(),
      status: "pending" as const,
      approverId: null,
      reviewReason: "",
      createdAt: new Date().toISOString(),
      reviewedAt: null,
    };
    state.requests.unshift(next);
    save(state);
    demoAuditAdapter.record({ actorId: actor.id, action: "refund.request", entityType: "refund_request", entityId: next.id, reason: `${calculationRule}；${input.reason.trim()}` });
    return clone(next);
  },
  async review(
    actor: SessionProfile,
    input: { refundId: string; decision: "approve" | "reject"; reason: string },
  ) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("只有店长或超管可以审批退款。");
    const state = load(),
      target =
        state.requests.find((x) => x.id === input.refundId) ??
        fail("退款申请不存在。");
    if (target.status !== "pending") fail("退款申请已经处理。");
    if (target.requestedBy === actor.id) fail("退款申请人不能自审。");
    if (actor.role === "manager" && target.storeId !== actor.storeId)
      fail("不能审批其他门店退款。");
    if (input.decision === "reject" && !input.reason.trim())
      fail("驳回必须填写原因。");
    target.status = input.decision === "approve" ? "approved" : "rejected";
    target.approverId = actor.id;
    target.reviewReason = input.reason.trim();
    target.reviewedAt = new Date().toISOString();
    if (input.decision === "approve")
      await demoSalesRefundAdapter.applyRefund(
        target.orderId,
        target.refundUnits,
      );
    save(state);
    demoAuditAdapter.record({ actorId: actor.id, action: `refund.${input.decision}`, entityType: "refund_request", entityId: target.id, reason: input.reason.trim() });
    demoNotificationAdapter.notify({
      recipientId: target.memberId,
      storeId: target.storeId,
      type: "refund",
      title: input.decision === "approve" ? "退款已批准" : "退款申请已驳回",
      content: target.reviewReason || "查看退款详情。",
      entityType: "refund_request",
      entityId: target.id,
      dedupeKey: `refund_request:${target.id}:refund`,
    });
    return clone(target);
  },
};
